/**
 * Upload Throttle Controller
 * 
 * Implementa limitación de velocidad real para subidas mediante
 * un sistema de pausado/reanudado automático basado en la velocidad actual.
 */

export interface ThrottleController {
  /** Current speed limit in bytes per second (null = unlimited) */
  speedLimit: number | null;
  /** Whether throttling is currently pausing the upload */
  isPausing: boolean;
  /** Start tracking an upload */
  start: () => void;
  /** Stop tracking */
  stop: () => void;
  /** Report bytes uploaded, returns delay in ms if should wait */
  reportProgress: (bytesUploaded: number) => number;
  /** Check if should pause based on current speed */
  shouldPause: () => boolean;
  /** Get current upload speed in bytes/second */
  getCurrentSpeed: () => number;
}

interface ThrottleState {
  startTime: number;
  lastCheckTime: number;
  lastBytes: number;
  totalBytes: number;
  samples: Array<{ time: number; bytes: number }>;
  isPausing: boolean;
}

/**
 * Creates a throttle controller for a single upload
 */
export function createThrottleController(
  speedLimit: number | null
): ThrottleController {
  const state: ThrottleState = {
    startTime: 0,
    lastCheckTime: 0,
    lastBytes: 0,
    totalBytes: 0,
    samples: [],
    isPausing: false,
  };

  // Keep last N samples for rolling average (last 2 seconds of data)
  const MAX_SAMPLES = 20;
  const SAMPLE_WINDOW_MS = 2000;

  function addSample(bytes: number) {
    const now = Date.now();
    state.samples.push({ time: now, bytes });
    
    // Remove old samples outside the window
    const cutoff = now - SAMPLE_WINDOW_MS;
    state.samples = state.samples.filter(s => s.time > cutoff);
  }

  function getCurrentSpeed(): number {
    if (state.samples.length < 2) return 0;
    
    const oldest = state.samples[0];
    const newest = state.samples[state.samples.length - 1];
    const timeDiff = (newest.time - oldest.time) / 1000; // seconds
    const bytesDiff = newest.bytes - oldest.bytes;
    
    if (timeDiff <= 0) return 0;
    return bytesDiff / timeDiff;
  }

  return {
    get speedLimit() {
      return speedLimit;
    },
    set speedLimit(limit: number | null) {
      speedLimit = limit;
    },
    get isPausing() {
      return state.isPausing;
    },

    start() {
      state.startTime = Date.now();
      state.lastCheckTime = Date.now();
      state.lastBytes = 0;
      state.totalBytes = 0;
      state.samples = [];
      state.isPausing = false;
    },

    stop() {
      state.samples = [];
      state.isPausing = false;
    },

    reportProgress(bytesUploaded: number): number {
      if (!speedLimit || speedLimit <= 0) {
        return 0; // No limit, no delay
      }

      addSample(bytesUploaded);
      state.totalBytes = bytesUploaded;

      const currentSpeed = getCurrentSpeed();
      
      if (currentSpeed <= 0 || currentSpeed <= speedLimit) {
        state.isPausing = false;
        return 0; // Within limit, no delay needed
      }

      // Calculate delay needed to bring average speed down to limit
      // If we're going too fast, we need to wait
      const excessSpeed = currentSpeed - speedLimit;
      const excessRatio = excessSpeed / speedLimit;
      
      // Delay proportional to how much we exceeded the limit
      // Max delay of 500ms to avoid long pauses
      const delay = Math.min(500, Math.floor(excessRatio * 200));
      
      if (delay > 50) {
        state.isPausing = true;
      }

      return delay;
    },

    shouldPause(): boolean {
      if (!speedLimit || speedLimit <= 0) {
        return false;
      }
      
      const currentSpeed = getCurrentSpeed();
      return currentSpeed > speedLimit * 1.1; // 10% tolerance
    },

    getCurrentSpeed,
  };
}

/**
 * Creates a throttled ReadableStream wrapper for a File/Blob
 * This can be used with fetch/XMLHttpRequest for real throttling
 */
export function createThrottledStream(
  file: Blob,
  speedLimit: number,
  onProgress?: (bytesRead: number) => void
): ReadableStream<Uint8Array> {
  const CHUNK_SIZE = 64 * 1024; // 64KB chunks
  let offset = 0;
  let lastTime = Date.now();
  let bytesInWindow = 0;
  const windowMs = 1000; // 1 second window

  return new ReadableStream({
    async pull(controller) {
      if (offset >= file.size) {
        controller.close();
        return;
      }

      const chunk = file.slice(offset, offset + CHUNK_SIZE);
      const buffer = await chunk.arrayBuffer();
      const uint8 = new Uint8Array(buffer);
      
      offset += uint8.byteLength;
      bytesInWindow += uint8.byteLength;
      onProgress?.(offset);

      const now = Date.now();
      const elapsed = now - lastTime;

      // Check if we need to throttle
      if (elapsed < windowMs && speedLimit > 0) {
        const currentSpeed = (bytesInWindow / elapsed) * 1000;
        
        if (currentSpeed > speedLimit) {
          // Calculate delay to bring speed down to limit
          const targetTime = (bytesInWindow / speedLimit) * 1000;
          const delay = Math.max(0, targetTime - elapsed);
          
          if (delay > 10) {
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }

      // Reset window every second
      if (elapsed >= windowMs) {
        lastTime = now;
        bytesInWindow = 0;
      }

      controller.enqueue(uint8);
    },
  });
}

/**
 * Throttle wrapper for TUS uploads using pause/resume
 */
export class ThrottledTusController {
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private lastBytes = 0;
  private lastTime = Date.now();
  private samples: Array<{ time: number; speed: number }> = [];
  private isPaused = false;
  
  constructor(
    private speedLimit: number | null,
    private pauseFn: () => void,
    private resumeFn: () => void
  ) {}

  setSpeedLimit(limit: number | null) {
    this.speedLimit = limit;
    if (!limit && this.isPaused) {
      this.resumeFn();
      this.isPaused = false;
    }
  }

  start() {
    this.lastTime = Date.now();
    this.lastBytes = 0;
    this.samples = [];
    this.isPaused = false;
    
    // Check speed every 200ms
    this.intervalId = setInterval(() => {
      if (!this.speedLimit || this.speedLimit <= 0) return;
      
      const avgSpeed = this.getAverageSpeed();
      
      if (avgSpeed > this.speedLimit * 1.2 && !this.isPaused) {
        // Going too fast, pause
        this.pauseFn();
        this.isPaused = true;
        
        // Resume after calculated delay
        const excessRatio = (avgSpeed - this.speedLimit) / this.speedLimit;
        const delay = Math.min(800, Math.floor(excessRatio * 300));
        
        setTimeout(() => {
          if (this.isPaused && this.speedLimit) {
            this.resumeFn();
            this.isPaused = false;
          }
        }, delay);
      }
    }, 200);
  }

  reportProgress(bytesUploaded: number) {
    const now = Date.now();
    const elapsed = now - this.lastTime;
    
    if (elapsed > 0) {
      const bytesDiff = bytesUploaded - this.lastBytes;
      const speed = (bytesDiff / elapsed) * 1000;
      
      this.samples.push({ time: now, speed });
      
      // Keep last 10 samples
      if (this.samples.length > 10) {
        this.samples.shift();
      }
    }
    
    this.lastBytes = bytesUploaded;
    this.lastTime = now;
  }

  private getAverageSpeed(): number {
    if (this.samples.length === 0) return 0;
    const sum = this.samples.reduce((acc, s) => acc + s.speed, 0);
    return sum / this.samples.length;
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.isPaused) {
      this.resumeFn();
      this.isPaused = false;
    }
  }
}
